"use client";

import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HowToOrder() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    number: "01",
    title: "SUBMIT SPECS",
    description: "Upload your project requirements or electrical schematics through our secure portal for a rapid review.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_submit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_submit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_submit.png-wvc-sizes",
    alt: "Digital blueprint on a screen"
  }, {
    number: "02",
    title: "EXPERT REVIEW",
    description: "Our engineering team validates your specifications to ensure full compliance with industry standards.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_review.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_review.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_review.png-wvc-sizes",
    alt: "Engineer reviewing technical documents"
  }, {
    number: "03",
    title: "PRECISION MARK",
    description: "We utilize advanced laser engraving and DPM technology to create durable, high-contrast identification.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_engrave.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_engrave.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_engrave.png-wvc-sizes",
    alt: "Laser engraving machine in operation"
  }, {
    number: "04",
    title: "FAST DELIVERY",
    description: "Your custom labels and tags are inspected, packaged, and shipped directly to your job site.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_ship.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_ship.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/order_step_ship.png-wvc-sizes",
    alt: "Finished industrial labels ready for shipping"
  }];
  return <section data-section-id="2506" id="how-to-order" className="bg-background text-foreground py-32 md:py-48 lg:py-[200px] px-6 md:px-10 lg:px-16 relative overflow-hidden">
      {}
      <div ref={headerRef} className={`relative transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center gap-3 mb-12 md:mb-16">
          <span className="font-mono text-xs uppercase tracking-widest text-foreground relative">
            <span className="relative z-10">02 / WORKFLOW</span>
            <span className="absolute left-0 right-0 -bottom-1 h-[2px] bg-primary"></span>
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12 md:mb-20">
          <div className="lg:col-span-8">
            <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[96px]">
              ORDERING
              <br />
              PROCESS
            </h2>
          </div>
          <div className="lg:col-span-4 lg:max-w-[380px]">
            <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80">From initial design submission to final site delivery — a streamlined workflow built for precision, compliance, and speed.</p>
          </div>
        </div>

        {}
        <div className="h-px w-full bg-foreground/80 mb-16 md:mb-24"></div>
      </div>

      {}
      <div ref={gridRef} className="relative">
        {}
        <div className="hidden lg:block absolute left-0 right-0 top-[240px] border-t border-dashed border-foreground/40 pointer-events-none z-0" aria-hidden="true"></div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6 lg:gap-6 relative z-10">
          {steps.map((step, i) => <div key={step.number} data-index={i} className={`group relative transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 120}ms`
        }}>
              {}
              <div className="relative bg-card backdrop-blur-md border border-border h-full min-h-[480px] flex flex-col transition-all duration-300 ease-out group-hover:-translate-y-2" style={{
            boxShadow: "12px 12px 0px 0px hsl(0 0% 0% / 0.9)"
          }}>
                <div className="absolute inset-0 transition-all duration-300 ease-out pointer-events-none group-hover:[box-shadow:24px_24px_0px_0px_hsl(0_0%_0%_/_0.9)]"></div>

                {}
                <div className="relative px-6 pt-8 pb-6">
                  <div className="font-default font-bold leading-none tracking-tighter text-transparent transition-colors duration-300 group-hover:text-primary" style={{
                fontSize: "120px",
                WebkitTextStroke: "2px hsl(0 0% 6%)"
              }}>
                    {step.number}
                  </div>
                </div>

                {}
                <div className="h-px w-full bg-border"></div>

                {}
                <div className="px-6 pt-6 pb-4 flex justify-center">
                  <div className="relative w-[200px] h-[200px] overflow-hidden border border-border">
                    <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-125" />
                    <div className="absolute inset-0 mix-blend-multiply pointer-events-none" style={{
                  backgroundColor: "hsl(45 68% 47% / 0.35)"
                }}></div>
                    <div className="absolute inset-0 mix-blend-screen pointer-events-none" style={{
                  backgroundColor: "hsl(0 0% 6% / 0.4)"
                }}></div>
                  </div>
                </div>

                {}
                <div className="px-6 pt-4">
                  <h3 className="font-default font-bold uppercase tracking-tight text-xl md:text-2xl text-foreground leading-tight">
                    {step.title}
                  </h3>
                </div>

                {}
                <div className="px-6 pt-3 pb-8 flex-1">
                  <p className="font-default text-base leading-relaxed text-foreground/80">
                    {step.description}
                  </p>
                </div>

                {}
                {i < steps.length - 1 && <div className="hidden lg:flex absolute -right-3 top-[240px] -translate-y-1/2 z-20 items-center justify-center w-6 h-6 bg-primary border border-border" aria-hidden="true">
                    <ArrowRight className="size-3.5 text-primary-foreground" strokeWidth={2.5} />
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}